import { z } from 'zod';

import { BorrowerEmailConsent } from '../Borrower';
import {
  AuthorizedContactId,
  BorrowerId,
  LoanNoteId,
  LoanWorkflowId,
  PaymentId,
  TeamSpecialistRoleId,
  UserId,
} from '../BrandedIds';
import { BaseLoanEvent } from './BaseLoanEvent';

const UpdateLoanIdsContext = z.object({
  type: z.literal('updateLoanIds'),
  updatedIds: z.array(z.string()),
});

const RemoveBankContext = z.object({
  type: z.literal('removeBank'),
});

const AddBankContext = z.object({
  type: z.literal('addBank'),
});

const InitiateMicrodepositsContext = z.object({
  type: z.literal('initiateMicrodeposits'),
});

const FirstLoginContext = z.object({
  type: z.literal('firstLogin'),
});

const MicrodepositEmailSentContext = z.object({
  type: z.literal('sentMicrodepositEmail'),
});

const ExpirePayoffContext = z.object({
  type: z.literal('expirePayoff'),
});

const CreateLoanNoteContext = z.object({
  type: z.literal('createLoanNote'),
  loanNoteId: LoanNoteId,
});

const BulkCreateLoanNotesContext = z.object({
  type: z.literal('bulkCreateLoanNotes'),
  loanNoteIds: z.array(LoanNoteId),
});

const PinLoanNoteContext = z.object({
  type: z.literal('pinLoanNote'),
  loanNoteId: LoanNoteId,
  isPinned: z.boolean(),
});

const CreateLoanWorkflowContext = z.object({
  type: z.literal('createLoanWorkflow'),
  loanWorkflowId: LoanWorkflowId,
  workflowType: z.enum(['foreclosure', 'realEstateOwned', 'chapter7', 'chapter13', 'delinquency']),
});

const CloseLoanWorkflowContext = z.object({
  type: z.literal('closeLoanWorkflow'),
  loanWorkflowId: LoanWorkflowId,
  reason: z.string(),
  comment: z.string().optional(),
});

export const AddUsioBankContext = z.object({
  type: z.literal('addUsioBank'),
  borrowerId: BorrowerId,
  accountName: z.string(),
});
export type AddUsioBankContext = z.infer<typeof AddUsioBankContext>;

const DeleteUsioBankContext = z.object({
  type: z.literal('deleteUsioBank'),
  borrowerId: BorrowerId,
  accountName: z.string(),
});

const EnableUsioBankContext = z.object({
  type: z.literal('enableUsioBank'),
  borrowerId: BorrowerId,
  accountName: z.string(),
});

const AddRepayBankContext = z.object({
  type: z.literal('addRepayBank'),
  borrowerId: BorrowerId,
  accountName: z.string(),
});

const EnableRepayBankContext = z.object({
  type: z.literal('enableRepayBank'),
  borrowerId: BorrowerId,
  accountName: z.string(),
});

const DeleteRepayBankContext = z.object({
  type: z.literal('deleteRepayBank'),
  borrowerId: BorrowerId,
  accountName: z.string(),
});

export const UpdateBorrowerEconsentContext = z.object({
  type: z.literal('updateBorrowerEconsent'),
  borrowerId: BorrowerId,
  econsentInput: BorrowerEmailConsent,
});
export type UpdateBorrowerEconsentContext = z.infer<typeof UpdateBorrowerEconsentContext>;

const UpdateLoanPointOfContactContext = z.object({
  type: z.literal('updateLoanPointOfContact'),
  previousUser: z.object({ id: UserId, name: z.string() }).optional(),
  newUser: z.object({ id: UserId, name: z.string() }).optional(),
  teamSpecialistRole: z.object({ id: TeamSpecialistRoleId, name: z.string() }).optional(),
  reason: z.enum(['teamDelete', 'teamUserRemoval', 'teamSpecialistRemoval']).optional(),
});
export type UpdateLoanPointOfContactContext = z.infer<typeof UpdateLoanPointOfContactContext>;

/* Authorized Contacts */
export const AddThirdPartyContactContext = z.object({
  type: z.literal('addThirdPartyContact'),
  authorizedContactId: AuthorizedContactId,
  name: z.string(),
});
export type AddThirdPartyContactContext = z.infer<typeof AddThirdPartyContactContext>;

export const EditThirdPartyContactContext = z.object({
  type: z.literal('editThirdPartyContact'),
  authorizedContactId: AuthorizedContactId,
  name: z.string(),
});
export type EditThirdPartyContactContext = z.infer<typeof EditThirdPartyContactContext>;

export const DeleteThirdPartyContactContext = z.object({
  type: z.literal('deleteThirdPartyContact'),
  name: z.string(),
});
export type DeleteThirdPartyContactContext = z.infer<typeof DeleteThirdPartyContactContext>;

export const AddLoanEntityContext = z.object({
  type: z.literal('addLoanEntity'),
});
export type AddLoanEntityContext = z.infer<typeof AddLoanEntityContext>;

export const EditLoanEntityContext = z.object({
  type: z.literal('editLoanEntity'),
});
export type EditLoanEntityContext = z.infer<typeof EditLoanEntityContext>;

export const DeleteLoanEntityContext = z.object({
  type: z.literal('deleteLoanEntity'),
  entityName: z.string(),
});
export type DeleteLoanEntityContext = z.infer<typeof DeleteLoanEntityContext>;

export const EditBorrowerDetailsContext = z.object({
  type: z.literal('editBorrowerDetails'),
  comment: z.string(),
});
export type EditBorrowerDetailsContext = z.infer<typeof EditBorrowerDetailsContext>;

const CancelPaymentContext = z.object({
  type: z.literal('cancelPayment'),
  paymentId: PaymentId,
});
export type CancelPaymentContext = z.infer<typeof CancelPaymentContext>;
/* ------- */

export const AddAuditLogItemEvent = BaseLoanEvent.extend({
  type: z.literal('addAuditLogItem'),
  context: z.discriminatedUnion('type', [
    UpdateLoanIdsContext,
    RemoveBankContext,
    AddBankContext,
    InitiateMicrodepositsContext,
    MicrodepositEmailSentContext,
    FirstLoginContext,
    ExpirePayoffContext,
    AddUsioBankContext,
    DeleteUsioBankContext,
    EnableUsioBankContext,
    AddRepayBankContext,
    DeleteRepayBankContext,
    EnableRepayBankContext,
    UpdateBorrowerEconsentContext,
    CreateLoanNoteContext,
    BulkCreateLoanNotesContext,
    PinLoanNoteContext,
    CreateLoanWorkflowContext,
    CloseLoanWorkflowContext,
    AddThirdPartyContactContext,
    EditThirdPartyContactContext,
    DeleteThirdPartyContactContext,
    AddLoanEntityContext,
    EditLoanEntityContext,
    DeleteLoanEntityContext,
    EditBorrowerDetailsContext,
    CancelPaymentContext,
    UpdateLoanPointOfContactContext,
  ]),
  comment: z.string().optional(),
});
export type AddAuditLogItemEvent = z.infer<typeof AddAuditLogItemEvent>;
